import { useCallback, useEffect, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useFormik } from "formik";

// @material-ui/core components
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  LinearProgress,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";

// @material-ui/lab components
import { Skeleton } from "@material-ui/lab";

// Karsten
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import MaskAndValid from "utils/maskAndValid";
import Api from "lib/api/httpRequestHelper";
import useLogs from "hooks/logs";

const RadioOption = withStyles(() => ({
  label: {
    fontSize: "1rem",
  },
  root: {
    marginBottom: 0,
  },
}))(FormControlLabel);

export default function DialogRegistroFalha({
  open,
  formType,
  onClose,
  selectedRecord,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [hasChange, setHasChange] = useState(false);
  const [details, setDetails] = useState(null);
  const [reasons, setReasons] = useState(null);
  const [reasonSelected, setReasonSelected] = useState(null);
  const [shiftOptions, setShiftOptions] = useState([]);

  const snackbar = useSnackbar();
  const { setLocalStorageApiProblem } = useLogs();

  const fetchMotivesList = useCallback(async () => {
    setFetchingDetails(true);

    await Api.get("weave-fail-motive-list", "weave", "", true)
      .then((res) => {
        setReasons(res.Result);
      })
      .catch((e) => {
        snackbar.showMessage(e, {
          severity: "error",
        });
      })
      .finally(() => setFetchingDetails(false));
  }, [snackbar]);

  const fetchDetails = useCallback(async () => {
    setFetchingDetails(true);

    await Api.get(
      "weave-register-detail",
      "weave",
      "?idLancamentoFalha=" + selectedRecord?.IdLancamentoFalha,
      true
    )
      .then((res) => {
        setFetchingDetails(false);
        setDetails(res.Result);
      })
      .catch((e) => {
        setFetchingDetails(false);
        snackbar.showMessage(e, {
          severity: "error",
        });
        setLocalStorageApiProblem({
          idMenuAccess: "135",
          screen: "Registrar Falha",
          problem: `Erro na chamada do endpoint weave-register-detail - ${e}`,
        });
      });
  }, [selectedRecord?.IdLancamentoFalha, snackbar]);

  const getShiftOptions = async () => {
    try {
      const response = await Api.get("weave-shift-list", "weave", "", true);

      setShiftOptions(response.Result);
    } catch (error) {
      console.log(error);
    } finally {
      setFetchingDetails(false);
    }
  };

  const submit = async (values) => {
    const {
      usuario,
      turno,
      numeroTear,
      pista,
      quantidadeFalha,
      observacao,
      motivo,
    } = values;
    const verifyReason = reasonSelected?.includes("rdume");

    setSubmitting(true);

    if (formType === "edit") {
      return await Api.put(
        "edit-fail-register",
        "weave",
        {
          idTurnoRegistro: turno.toString(),
          quantidadeFalha: quantidadeFalha.toString(),
          usuarioLancamento: usuario.toString(),
          idLancamentoFalha: selectedRecord?.IdLancamentoFalha.toString(),
          idTear: numeroTear.toString(),
          idPista: verifyReason ? pista.toString() : "",
          idMotivoFalha: motivo.toString(),
          observacao,
        },
        true
      )
        .then((res) => {
          if (res.Success) {
            snackbar.showMessage(`Registro de falha editado com sucesso!`, {
              severity: "success",
            });
            handleClose(res.Result);
          } else {
            snackbar.showMessage(
              `Ocorreu um erro ao editar o registro de falha. Verifique os parâmetros informados.`,
              {
                severity: "error",
              }
            );
            setSubmitting(false);
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });
          setSubmitting(false);
        });
    } else {
      return await Api.post(
        "create-fail-register",
        "weave",
        {
          idTurnoRegistro: turno.toString(),
          quantidadeFalha: quantidadeFalha.toString(),
          usuarioLancamento: usuario.toString(),
          idTear: numeroTear.toString(),
          idPista: verifyReason ? pista.toString() : "",
          idMotivoFalha: motivo.toString(),
          observacao,
        },
        true
      )
        .then((res) => {
          if (res.Success) {
            snackbar.showMessage(`Registro de falha cadastrado com sucesso!`, {
              severity: "success",
            });
            handleClose(res.Result);
          } else {
            snackbar.showMessage(
              `Ocorreu um erro ao registrar falha. Verifique os parâmetros informados.`,
              {
                severity: "error",
              }
            );
            setSubmitting(false);
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });
          setSubmitting(false);
        });
    }
  };

  const formik = useFormik({
    initialValues: {
      turno: selectedRecord?.TearTurnoRegistro?.IdTurno || "",
      quantidadeFalha: selectedRecord?.QuantidadeFalha || "",
      usuario: selectedRecord?.UsuarioLancamento || "",
      numeroTear: selectedRecord?.IdTear || "",
      pista: selectedRecord?.IdPista?.toString() || "1",
      observacao: selectedRecord?.Observacao || "",
      motivo: selectedRecord?.IdMotivoFalha || "",
    },
    onSubmit: submit,
  });

  const { values, handleChange, handleSubmit, resetForm } = formik;

  const handleClose = (editedRecord) => {
    resetForm();
    onClose(editedRecord);
  };

  useEffect(() => {
    // fetchDetails();
    fetchMotivesList();
    getShiftOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            <span>
              <Typography id="alert-dialog-title" variant="h3" component="h3">
                {`${
                  formType === "edit" ? "Editar" : "Registrar"
                } Registro de Falha`}
              </Typography>
            </span>
          </DialogTitle>
          <PerfectScrollbar>
            <DialogContent style={{ padding: "1rem 1.5rem 1.5rem" }}>
              <>
                <Grid container>
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormLabel required>Usuário</FormLabel>
                      <Tooltip title="Informe o usuário" placement="bottom">
                        <TextField
                          name="usuario"
                          type="number"
                          required
                          fullWidth
                          disabled={submitting}
                          value={values.usuario}
                          variant="outlined"
                          onChange={handleChange}
                          onInput={(e) => {
                            setHasChange(true);
                            e.target.value = e.target.value
                              .toString()
                              .slice(0, 50);
                          }}
                          inputProps={{ min: 0 }}
                        />
                      </Tooltip>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormLabel>Turno</FormLabel>
                      <FormControl
                        variant="outlined"
                        fullWidth
                        disabled={submitting || shiftOptions.length === 0}
                      >
                        <Select
                          name="turno"
                          fullWidth
                          value={values.turno}
                          onChange={handleChange}
                          IconComponent={KeyboardArrowDown}
                        >
                          {shiftOptions.map((option) => (
                            <MenuItem
                              key={option.IdTurno}
                              value={option.IdTurno}
                            >
                              {option.Descricao}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormLabel required>Número Tear</FormLabel>
                      <TextField
                        name="numeroTear"
                        type="number"
                        required
                        fullWidth
                        disabled={submitting}
                        value={values.numeroTear}
                        variant="outlined"
                        onChange={handleChange}
                        onInput={(e) => {
                          setHasChange(true);
                          e.target.value = e.target.value
                            .toString()
                            .slice(0, 10);
                        }}
                        inputProps={{ min: 0 }}
                      />
                    </FormGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <FormLabel required>Motivo da Falha</FormLabel>
                    {reasons ? (
                      <RadioGroup
                        aria-label="opções de motivos de falha"
                        required
                        name="motivo"
                        value={values.motivo.IdMotivoFalha}
                        onClick={() => setHasChange(true)}
                        onChange={handleChange}
                        disabled={submitting}
                        style={{ display: "flex", flexDirection: "column" }}
                      >
                        {reasons.map((reason) => {
                          return (
                            <RadioOption
                              key={reason.IdMotivoFalha}
                              value={reason.IdMotivoFalha}
                              control={
                                <Radio
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    setReasonSelected(reason.DescMotivoFalha)
                                  }
                                  checked={
                                    reason.IdMotivoFalha ===
                                    parseInt(values.motivo)
                                  }
                                />
                              }
                              label={reason.DescMotivoFalha}
                            />
                          );
                        })}
                      </RadioGroup>
                    ) : (
                      <FormGroup>
                        <Skeleton />
                      </FormGroup>
                    )}
                  </Grid>
                  {reasonSelected?.includes("rdume") && (
                    <Grid item xs={12}>
                      <FormLabel required>Pista</FormLabel>
                      <RadioGroup
                        aria-label="opções de pista"
                        required
                        name="pista"
                        value={values.pista}
                        onChange={handleChange}
                        disabled={submitting}
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <RadioOption
                          value="1"
                          control={<Radio size="small" color="primary" />}
                          label="1"
                        />
                        <RadioOption
                          value="2"
                          control={<Radio size="small" color="primary" />}
                          label="2"
                        />
                        <RadioOption
                          value="3"
                          control={<Radio size="small" color="primary" />}
                          label="3"
                        />
                        <RadioOption
                          value="4"
                          control={<Radio size="small" color="primary" />}
                          label="4"
                        />
                        <RadioOption
                          value="5"
                          control={<Radio size="small" color="primary" />}
                          label="5"
                        />
                      </RadioGroup>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormLabel required>Quantidade de Falhas</FormLabel>
                      <Tooltip title="Informe o usuário" placement="bottom">
                        <TextField
                          name="quantidadeFalha"
                          type="number"
                          required
                          fullWidth
                          disabled={submitting}
                          value={values.quantidadeFalha}
                          variant="outlined"
                          onChange={handleChange}
                          onInput={(e) => {
                            setHasChange(true);
                            e.target.value = e.target.value
                              .toString()
                              .slice(0, 4);
                          }}
                          inputProps={{ min: 0 }}
                        />
                      </Tooltip>
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormLabel>Observação</FormLabel>
                      <TextField
                        name="observacao"
                        fullWidth
                        disabled={submitting}
                        value={values.observacao}
                        variant="outlined"
                        onChange={handleChange}
                        onInput={(e) => {
                          setHasChange(true);
                          e.target.value = e.target.value
                            .toString()
                            .slice(0, 50);
                        }}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
                {submitting && (
                  <div style={{ marginTop: "1rem" }}>
                    <span>{`${
                      formType === "create" ? "Cadastrando" : "Editando"
                    } registro de falha...`}</span>
                    <LinearProgress />
                  </div>
                )}
              </>
            </DialogContent>
          </PerfectScrollbar>
          <DialogActions>
            <Button
              component={Box}
              disabled={submitting}
              onClick={() => {
                setHasChange(false);
                handleClose();
              }}
            >
              Cancelar
            </Button>

            <Button
              color="default"
              variant="contained"
              type="submit"
              disabled={
                !hasChange ||
                submitting ||
                fetchingDetails ||
                MaskAndValid.isNullOrEmpty(values.usuario) ||
                MaskAndValid.isNullOrEmpty(values.numeroTear) ||
                parseInt(values.numeroTear) === 0 ||
                MaskAndValid.isNullOrEmpty(values.pista) ||
                MaskAndValid.isNullOrEmpty(values.motivo)
              }
            >
              {formType === "edit" ? "Atualizar" : "Registrar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
